import { overEvery, overSome, identity, negate } from 'lodash/fp';
import { INCOMING_EMAIL } from '@/constants';
import {
  EXTERNALLY_DISCONNECTED,
  INTERNALLY_DISCONNECTED,
  UNASSIGNED,
} from '@/constants/interaction-states';
import { casCloseContactSubStatusMap } from '@/config/cas';

const hasNoQuestionsAndIsNotClosed = (
  state,
  { hasClosedStatus, hasQuestion },
) => !hasQuestion && !hasClosedStatus;

const isRecall = (state, getters, rootState, rootGetters) =>
  rootGetters['cti/isRecall'];

const allCallbacksHaveStatuses = (state, { callbacks = [] }) =>
  callbacks.every(({ status, description }) =>
    [status, description].every(value => value != null),
  );

const recallCallHasDisconnected = (state, getters, rootState, rootGetters) => {
  if (!rootGetters['cti/isRecall']) {
    return true;
  }

  const recallInteractions = rootGetters['cti/recallInteractions'] || [];

  // Find the actual caller, it has both a conversation ID and a CRM external ID.
  const callerInteraction = recallInteractions.find(
    i => i.interactionConversationId && i.interactionExternalId,
  );

  const interaction = callerInteraction ?? recallInteractions[recallInteractions.length - 1];

  if (!interaction) {
    console.log('Cannot detect recall interaction state, no recall interaction found');
    return false;
  }

  return [
    EXTERNALLY_DISCONNECTED,
    INTERNALLY_DISCONNECTED,
    UNASSIGNED,
  ].includes(interaction.interactionState);
};

/**
 * Allow closing CAS contacts without questions when the selected sub status
 * is one of the allowed CAS close sub statuses. In these cases there is no
 * question, so the agent cannot indicate whether the CAS suggestion was used.
 */
const hasAllowedCasSubStatusWithoutQuestion = (state, rootGetters) => {
  const { contact } = state || {};
  if (!contact) return false;

  if (contact.channel !== INCOMING_EMAIL) return false;

  const subStatusId = contact.subStatus;
  if (!subStatusId) return false;

  const getSubStatusByTypeAndSubStatusId =
    rootGetters['metadata/getSubStatusByTypeAndSubStatusId'];
  if (!getSubStatusByTypeAndSubStatusId) return false;

  const subStatus = getSubStatusByTypeAndSubStatusId(
    'contactStatuses',
    subStatusId,
  );
  const selectedSubStatusName = subStatus?.name?.toLowerCase?.();
  if (!selectedSubStatusName) return false;

  const allowedSubStatuses = Object.values(casCloseContactSubStatusMap);

  return allowedSubStatuses.includes(selectedSubStatusName);
};

const validations = [
  {
    predicate: (state, { isLoading }) => !isLoading,
    name: 'contact_update_pending',
  },
  {
    predicate: (state, { contactId }) => identity(contactId),
    name: 'contact_not_active',
  },
  {
    predicate: (state, { channelIsChat }, rootState, rootGetters) =>
      !(channelIsChat && rootGetters['cti/isParentInteractionConnected']),
    name: 'contact_close_chat',
  },
  {
    predicate: recallCallHasDisconnected,
    name: 'contact_closing_not_possible_when_recall_call_active',
  },
  {
    predicate: overSome([
      overEvery([allCallbacksHaveStatuses, isRecall]),
      negate(hasNoQuestionsAndIsNotClosed),
    ]),
    name: 'contact_closing_not_possible_when_status_is_not_closed',
  },
  {
    predicate: (state, { allQuestionsAreValid }) => allQuestionsAreValid,
    name: 'contact_closing_not_possible_when_questions_are_not_complete',
  },
  {
    predicate: (
      { hasSavedEndRegistration },
      { hasQuestion, requiredEndRegistrationFields },
    ) =>
      hasSavedEndRegistration ||
      requiredEndRegistrationFields.length === 0 ||
      !hasQuestion,
    name: 'contact_closing_not_possible_when_missing_final_registration',
  },
  {
    predicate: (state, getters, rootState, rootGetters) => {
      const { contact } = state || {};
      if (!contact) return true;
      if (contact.channel !== INCOMING_EMAIL) return true;

      if (hasAllowedCasSubStatusWithoutQuestion(state, rootGetters)) {
        return true;
      }

      const phase = getters.getCasPhaseChoice;
      if (phase !== 'Fase3') return true;

      if (!contact.casSuggestion) return true;

      // The Yes/No "use suggestion" choice is only surfaced for the
      // Direct Antwoord action. For other actions (Escalatie,
      // Dossieropzoeking, ...) no choice is required to close.
      if (contact.casSuggestion.Action !== 'Direct Antwoord') return true;

      return typeof contact.CasSuggestionUsed === 'boolean';
    },
    name: 'contact_closing_not_possible_when_missing_cas_choice',
  },
  {
    predicate: (state, { hasDraftEmails }) => !hasDraftEmails,
    name: 'contact_closing_not_possible_when_emails_in_draft',
  },
  {
    predicate: (state, { hasActiveScreensharingSessions }) =>
      !hasActiveScreensharingSessions,
    name: 'contact_closing_not_possible_when_screensharingsession_active',
  },
];

export default (state, getters, rootState, rootGetters) => {
  const getLabelByName = rootGetters['metadata/getLabelByName'];

  return validations.reduce((acc, { predicate, name }) => {
    if (acc || predicate(state, getters, rootState, rootGetters)) {
      return acc;
    }

    const label = getLabelByName(name);

    return label.body;
  }, null);
};
